// Install-context detection + session probe. Drives which "no session" screen to
// show: installed app → pairing (state 2); browser tab → install (state 2b).

export type DisplayContext = "standalone" | "browser";

export function displayContext(): DisplayContext {
  const standalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari legacy flag for home-screen web apps.
    (navigator as unknown as { standalone?: boolean }).standalone === true;
  return standalone ? "standalone" : "browser";
}

// Ask the server whether this device holds a valid (non-revoked) session. The session
// is an httpOnly cookie we can't read; only the server knows. Returns the masked email
// + avatar initials (both derived server-side from the full address) when paired.
// `offline: true` = the probe never reached the server (transport error) — callers must
// NOT treat that as "not paired" when they hold a cached session.
export type UsageGroup = { key: string; label: string; calls: number; eur: number };
export type OrgLimit = { limit_eur: number | null; spent_retail_eur: number; remaining_eur: number | null; pct: number | null; markup_pct: number };
export type UsageInfo = {
  metering: "self_serve" | "org"; // 'org' → hide allowance/wallet; show the per-user limit (org) instead
  allowance_eur: number; spent_eur: number; pct: number; wallet_eur: number; over: boolean;
  resets_at: string;
  org: OrgLimit | null; // org per-user limit (only when metering==='org'); spend at the org markup
  groups: UsageGroup[];
  emails_sent: number;
  storage?: { used_bytes: number; cap_bytes: number } | null; // doc-storage meter (BACKLOG #009); optional — older server = row hidden
};
// NOTE: org enrollment STATE (invited / discovery / accept-decline) is deliberately not consumed on
// the companion (Gery 2026-06-23) — you can only choose a plan in the Outlook pane / desktop PWA. The
// server may still send an `org` membership field on /api/session; the companion simply ignores it.
export type SessionProbe = { paired: boolean; email?: string; initials?: string; offline?: boolean; number_format?: "eu" | "us"; audio_feedback?: boolean; usage?: UsageInfo | null };
export async function probeSession(): Promise<SessionProbe> {
  try {
    const r = await fetch("/api/session", { method: "GET", credentials: "include" });
    if (!r.ok) return { paired: false };
    return (await r.json()) as SessionProbe;
  } catch {
    return { paired: false, offline: true };
  }
}
